// /workflows — Business Workflows, first-class in cortex.
//
// One nav surface, three tabs:
//   My Work    — the task inbox: steps waiting on the signed-in user (by role
//                or direct assignment), start published processes, follow runs.
//   Processes  — the visual process builder: a ReactFlow canvas (same stack +
//                zoom/pan as the Pipeline canvas) over graphs of start / step /
//                decision / end nodes assigned to roles, users or AGENT actors;
//                per-process Triggers (email listeners) and Runs.
//   Roles      — role → member management for task routing.
//
// All data goes through POST /api/wf/engine (backend allowlist + identity
// stamping) to the per-project process engine cortex.workflows.api.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isPortal } from "../utils/portal";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { wfEngine } from "../services/workflows";
import WorkflowCanvas, { WF_NODE_META, wfEdgeId, CompositionMap } from "../components/workflows/WorkflowCanvas";
import { resumeFileToText } from "../components/workflows/docxText";
import { listViews } from "../services/pipeline";
import { createPageUrl } from "@/utils";
import {
  Inbox, ListChecks, Users, UserRound, Bot, Play, Loader2, RefreshCw, CheckCircle2,
  ChevronRight, ChevronDown, Plus, X, Trash2, Save, Rocket, GitBranch, Square, Flag,
  PencilLine, AlertTriangle, Mail, Zap, ArrowRight, Route, CircleDot, Upload, FileText, Radio,
  Sparkles, Wrench, PanelLeftClose, PanelLeftOpen, Clock, Code2, ExternalLink,
} from "lucide-react";
import AppRequestDialog from "../components/AppRequestDialog";
import { useUiView } from "@/hooks/useUiView";
import VersionHistory from "../components/VersionHistory";

// ── shared UI atoms (cortex tokens, same shapes as Apps/Pipeline) ────────────
const inputCls = "w-full rounded-md border border-card-border bg-page-bg px-3 py-2 text-sm text-page-text outline-none focus:border-nav-active";
const inputSm = "rounded-md border border-card-border bg-page-bg px-2 py-1.5 text-sm text-page-text outline-none focus:border-nav-active";
const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 disabled:opacity-50";
const btnDanger = "inline-flex items-center gap-1.5 rounded-md text-xs px-2.5 py-1 border border-card-border bg-transparent text-page-muted cursor-pointer hover:border-red-400 hover:text-red-600";
const card = "rounded-xl border border-card-border bg-card-bg";

const STATUS_COLOR = { draft: "#94a3b8", published: "#34d399", running: "#818cf8", completed: "#34d399", cancelled: "#cbd5e1", open: "#60a5fa", done: "#34d399", skipped: "#cbd5e1" };
function StatusBadge({ s }) {
  const c = STATUS_COLOR[s] || "#94a3b8";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium capitalize" style={{ background: c + "22", color: c }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{s}
    </span>
  );
}
function jsonish(v, fb) {
  if (v == null) return fb;
  if (typeof v === "string") { try { return JSON.parse(v); } catch { return fb; } }
  return v;
}
function fmtTime(v) {
  try { return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return String(v || ""); }
}
function errMsg(e) { return String(e?.message || e?.error || e || "request failed"); }

function Assignee({ t, size = 11 }) {
  if (t.subprocess) return <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: "#db2777" }}><Route size={size} />sub-process</span>;
  if (t.endpoint) return <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: "#0891b2" }}><Code2 size={size} />code: {t.endpoint}</span>;
  if (t.agent) return <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: "#8b5cf6" }}><Bot size={size} />agent: {t.agent}</span>;
  if (t.assignee) return <span className="inline-flex items-center gap-1 text-[11px] text-page-muted"><UserRound size={size} />{t.assignee}</span>;
  if (t.role) return <span className="inline-flex items-center gap-1 text-[11px] text-page-muted"><Users size={size} />{t.role}</span>;
  return null;
}

// ── run context (what earlier steps produced, incl. the triggering email) ────
function InstanceContext({ data }) {
  const d = jsonish(data, {}) || {};
  const evt = d._event;
  const keys = Object.keys(d).filter((k) => k[0] !== "_");
  if (!keys.length && !evt && !d._start) return null;
  return (
    <div className="mt-2 rounded-lg bg-card-border/20 p-2.5 space-y-1.5">
      {evt ? (
        <div className={`text-xs ${card} p-2`}>
          <div className="flex items-center gap-1.5 font-medium text-page-text"><Mail size={11} className="text-nav-active" />Triggering email</div>
          {evt.from ? <div className="text-page-muted mt-0.5">from: {evt.from}</div> : null}
          {evt.subject ? <div className="text-page-muted">subject: {evt.subject}</div> : null}
          {evt.body ? <div className="text-page-muted mt-1 whitespace-pre-wrap max-h-40 overflow-y-auto">{String(evt.body).slice(0, 1200)}</div> : null}
        </div>
      ) : null}
      {d._start?.note ? <div className="text-xs text-page-muted">“{d._start.note}” — {d._start.by}</div> : null}
      {keys.map((k) => {
        const v = d[k] || {};
        const fd = v.form_data || {};
        const fdKeys = Object.keys(fd);
        return (
          <div key={k} className="text-xs text-page-text">
            <span className="font-medium">{v.title || k}</span>
            {v.outcome ? <span className="text-amber-600 font-medium"> → {v.outcome}</span> : null}
            {v.by ? <span className="text-page-muted"> · {v.by}</span> : null}
            {v.note ? <div className="text-page-muted ml-2">“{v.note}”</div> : null}
            {fdKeys.length ? <div className="text-page-muted ml-2">{fdKeys.map((fk) => `${fk}: ${Array.isArray(fd[fk]) ? fd[fk].join(" • ") : fd[fk]}`).join(" · ")}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

// ── generic trigger form: upload a document → ingest → run ──────────────────
// Rendered ENTIRELY from the listener's config.form spec ({title, hint,
// accept[], document}) — no per-workflow code. The document is ingested RAW;
// interpreting it (names, emails, amounts, ...) is the agents' job, per their
// manifests' execution contracts.
function RunProcessModal({ listener, onClose, onStarted, onEdit }) {
  const { user } = useAuth();
  const cfg = jsonish(listener?.config, {}) || {};
  const spec = cfg.form || {};
  // Upload is ONLY for manual listeners that declare a form (the process
  // genuinely consumes a user document, e.g. a sourcing brief). Every other
  // trigger — schedule, data_condition, form-less manual — runs with one
  // click: the agents prepare the context from the governed data themselves.
  const hasForm = listener?.kind === "manual" && !!(spec.title || spec.document || spec.accept);
  const accept = (Array.isArray(spec.accept) && spec.accept.length ? spec.accept : [".docx", ".txt", ".md"]).join(",");
  const docWord = spec.document || "document";
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      setErr("");
      const t = await resumeFileToText(f);
      if (!t.trim()) { setErr("Could not read any text from that file."); return; }
      setFile(f);
      setText(t);
    } catch (ex) { setErr(errMsg(ex)); }
  };
  const start = async () => {
    if (!text.trim()) { setErr(`Upload a ${docWord} file first.`); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        filename: file?.name || null,
        body: text,
        chars: text.length,
        source: "upload_form",
        from: user?.email || "upload_form",
        subject: `${spec.title || "Upload"} — ${file?.name || docWord}`,
        uploaded_by: user?.email || null,
      };
      const r = await wfEngine("ingest_event", { listener_id: listener.id, dedup_key: `manual-${Date.now()}`, payload });
      setBusy(false);
      onStarted(r.instance.id);
    } catch (e) { setBusy(false); setErr(errMsg(e)); }
  };
  const runNow = async () => {
    setBusy(true); setErr("");
    try {
      const r = await wfEngine("start_process", { definition_id: listener.definition_id, by: user?.email, note: "manual run" + (cfg.label ? ` (${cfg.label})` : "") });
      setBusy(false);
      onStarted(r.instance.id);
    } catch (e) { setBusy(false); setErr(errMsg(e)); }
  };
  if (!hasForm) {
    const kindLabel = listener?.kind === "schedule" ? `runs ${cfg.every || "on schedule"} automatically`
      : listener?.kind === "data_condition" ? "fires automatically when the data condition matches"
      : listener?.kind === "email" ? "starts from matching email" : "starts on demand";
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className={`relative ${card} shadow-xl p-5 space-y-4`} style={{ width: 470, maxWidth: "calc(100vw - 32px)" }} data-tour="wf-run-form">
          <div className="flex items-center gap-2">
            {listener?.kind === "schedule" ? <Clock size={16} style={{ color: "#f59e0b" }} /> : <Zap size={16} style={{ color: "#a3e635" }} />}
            <div className="font-semibold text-page-text">{cfg.label || (listener?.kind === "schedule" ? "Schedule" : "Trigger")}</div>
            <span className="text-xs text-page-muted">starts “{cfg.position_title || "this process"}”</span>
            <div className="flex-1" />
            <button onClick={onClose} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer"><X size={16} /></button>
          </div>
          {cfg.description ? <div className="text-sm text-page-muted">{cfg.description}</div> : null}
          <div className="flex items-center gap-2">
            {err ? <span className="text-xs text-red-600">{err}</span>
              : <span className="text-xs text-page-muted">This trigger {kindLabel}. You can also start a run right now — the agents prepare everything from the project's data.</span>}
            <div className="flex-1" />
            {onEdit ? (
              <button className={btnGhost} onClick={onEdit} data-wf-edit-trigger-link title="Edit this trigger's condition / schedule in the Triggers tab">
                <PencilLine size={13} />Edit trigger
              </button>
            ) : null}
            <button className={btnPrimary} disabled={busy} onClick={runNow} data-tour="wf-run-start">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}Run now
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative ${card} shadow-xl p-5 space-y-4`} style={{ width: 470, maxWidth: "calc(100vw - 32px)" }} data-tour="wf-run-form">
        <div className="flex items-center gap-2">
          <Upload size={16} style={{ color: "#22d3ee" }} />
          <div className="font-semibold text-page-text">{spec.title || "Upload"}</div>
          <span className="text-xs text-page-muted">starts “{cfg.position_title || "this process"}”</span>
          <div className="flex-1" />
          <button onClick={onClose} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer"><X size={16} /></button>
        </div>
        <label
          className="block rounded-xl border-2 border-dashed border-card-border hover:border-nav-active transition-colors cursor-pointer text-center px-4 py-8"
          data-tour="wf-resume-drop"
        >
          <input type="file" accept={accept} className="hidden" onChange={onFile} data-wf-resume-file />
          {!file ? (
            <>
              <Upload size={26} className="mx-auto text-page-muted" />
              <div className="mt-2 text-sm font-medium text-page-text">Upload the {docWord}</div>
              <div className="mt-1 text-xs text-page-muted">{spec.hint || `${accept.replaceAll(",", ", ")} — the process starts on upload`}</div>
            </>
          ) : (
            <>
              <FileText size={24} className="mx-auto" style={{ color: "#22d3ee" }} />
              <div className="mt-2 text-sm font-medium text-page-text" data-wf-file-name>{file.name}</div>
              <div className="mt-1 text-[11px] text-page-muted">{text.length.toLocaleString()} characters read · click to replace</div>
            </>
          )}
        </label>
        <div className="flex items-center gap-2">
          {err ? <span className="text-xs text-red-600">{err}</span> : <span className="text-xs text-page-muted">The workflow's agents read everything they need from the {docWord}.</span>}
          <div className="flex-1" />
          <button className={btnPrimary} disabled={busy || !text.trim()} onClick={start} data-tour="wf-run-start">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}Start
          </button>
        </div>
      </div>
    </div>
  );
}

// ── live run panel: the run's output, reviewable (and actionable) in place ──
function RunPanel({ run, graph, onAct, onClose }) {
  const ins = run?.instance;
  const tasks = run?.tasks || [];
  const data = jsonish(ins?.data, {}) || {};
  const evt = data._event || {};
  const fired = jsonish(ins?.fired, {}) || {};
  const endReached = (graph.nodes || []).find((n) => n.type === "end" && (graph.edges || []).some((e) => e.to === n.id && fired[wfEdgeId(e)]));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const act = async (task, outcome) => {
    setBusy(task.id + (outcome || "")); setErr("");
    try {
      const p = { id: task.id, note: note.trim() || undefined };
      if (outcome) p.outcome = outcome;
      await wfEngine("complete_task", p);
      setNote(""); setBusy(""); onAct();
    } catch (e) { setBusy(""); setErr(errMsg(e)); }
  };
  return (
    <div className="p-3 space-y-3" data-tour="wf-run-panel">
      <div className="flex items-center gap-2">
        <Radio size={14} style={{ color: ins?.status === "running" ? "#60a5fa" : "#34d399" }} className={ins?.status === "running" ? "animate-pulse" : ""} />
        <span className="font-semibold text-sm">Run</span>
        <StatusBadge s={ins?.status || "running"} />
        <div className="flex-1" />
        <button className="bg-transparent border-0 cursor-pointer" style={{ color: "var(--flow-text-muted)" }} onClick={onClose} title="Exit run view"><X size={14} /></button>
      </div>
      {evt.from ? (
        <div className="rounded-md p-2 text-xs" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid var(--flow-border,#334155)" }}>
          <div className="font-medium">{evt.from}</div>
          {evt.subject ? <div style={{ color: "var(--flow-text-muted)" }}>{evt.subject}</div> : null}
          {evt.source === "upload_form" ? <div style={{ color: "#22d3ee", marginTop: 2 }}>via upload form{evt.uploaded_by ? ` · ${evt.uploaded_by}` : ""}</div> : null}
        </div>
      ) : null}
      {ins?.status !== "running" ? (
        <div className="rounded-md p-2 text-xs font-medium" style={{ background: ins?.status === "completed" ? "rgba(52,211,153,0.12)" : "rgba(148,163,184,0.12)", border: "1px solid rgba(52,211,153,0.4)", color: ins?.status === "completed" ? "#6ee7b7" : "var(--flow-text-muted)" }}
          data-wf-run-finished={endReached?.id || ins?.status}>
          {ins?.status === "completed" ? <>Finished: <b>{endReached?.title || "done"}</b></> : "Run cancelled"}
        </div>
      ) : null}
      {tasks.map((t) => {
        const fd = jsonish(t.form_data, {}) || {};
        const outcomes = jsonish(t.outcomes, null);
        const mine = !t.agent && !t.endpoint && !t.subprocess && t.status === "open";
        return (
          <div key={t.id} className="rounded-md p-2.5 space-y-1.5 text-xs" data-wf-run-task={t.node_id}
            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${t.status === "open" ? "#60a5fa66" : "var(--flow-border,#334155)"}` }}>
            <div className="flex items-center gap-1.5 flex-wrap">
              <StatusBadge s={t.status} />
              <span className="font-medium">{t.title}</span>
              {t.outcome ? <span style={{ color: "#fbbf24", fontWeight: 700 }}>→ {t.outcome}</span> : null}
            </div>
            <div style={{ color: "var(--flow-text-muted)" }}>
              {t.subprocess ? <span style={{ color: "#f9a8d4" }}>🔁 sub-process{t.child_instance_id ? ` · run ${t.child_instance_id}` : ""}{t.status === "open" ? " · running…" : ""}</span>
                : t.endpoint ? <span style={{ color: "#67e8f9" }}>⚡ {t.endpoint}{t.status === "open" ? " · running the endpoint…" : ""}</span>
                : t.agent ? <span style={{ color: "#b79df8" }}>🤖 {t.agent}{t.status === "open" ? (t.claimed_by ? " · working…" : " · waiting for the agent worker") : ""}</span>
                : t.role ? `role: ${t.role}` : t.assignee}
              {t.completed_by ? ` · by ${t.completed_by}` : ""}
            </div>
            {t.status === "open" && (t.agent || t.endpoint || t.subprocess) ? (() => {
              const pr = jsonish(t.progress, []) || [];
              return pr.length ? (
                <div className="space-y-0.5" data-wf-progress>
                  {pr.slice(-3).map((p2, i) => (
                    <div key={i} style={{ color: i === pr.slice(-3).length - 1 ? "#93c5fd" : "var(--flow-text-muted)", fontSize: 11 }}>
                      {i === pr.slice(-3).length - 1 ? "▸ " : "· "}{p2.msg}
                    </div>
                  ))}
                </div>
              ) : null;
            })() : null}
            {typeof fd.score === "number" ? (
              <div>
                <div className="flex justify-between" style={{ color: "var(--flow-text-muted)" }}><span>fit score</span><span style={{ color: fd.score >= (fd.threshold ?? 70) ? "#34d399" : "#f87171", fontWeight: 700 }}>{fd.score}/100</span></div>
                <div style={{ height: 5, borderRadius: 4, background: "rgba(255,255,255,0.09)", marginTop: 3 }}>
                  <div style={{ height: 5, borderRadius: 4, width: `${Math.min(100, fd.score)}%`, background: fd.score >= (fd.threshold ?? 70) ? "#34d399" : "#f87171" }} />
                </div>
              </div>
            ) : null}
            {fd.reasoning ? <div style={{ color: "var(--flow-text-muted)" }}>{String(fd.reasoning).slice(0, 400)}</div> : null}
            {Array.isArray(fd.questions) && fd.questions.length ? (
              <ol className="pl-4 space-y-1 m-0" style={{ listStyle: "decimal" }} data-wf-questions>
                {fd.questions.map((q, i) => <li key={i}>{q}</li>)}
              </ol>
            ) : null}
            {Object.keys(fd).filter((k) => !["score", "threshold", "reasoning", "questions"].includes(k)).map((k) => (
              <div key={k} style={{ color: "var(--flow-text-muted)" }} data-wf-fd={k}>
                <span style={{ color: "var(--flow-text)", fontWeight: 600 }}>{k.replace(/_/g, " ")}:</span>{" "}
                {Array.isArray(fd[k]) ? fd[k].join(" • ") : typeof fd[k] === "boolean" ? (fd[k] ? "yes" : "no") : typeof fd[k] === "object" && fd[k] !== null ? JSON.stringify(fd[k]) : String(fd[k])}
              </div>
            ))}
            {t.note ? <div style={{ color: "var(--flow-text-muted)" }}>“{t.note}”</div> : null}
            {mine ? (
              <div className="space-y-1.5 pt-1">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Comment (optional)"
                  style={{ width: "100%", background: "rgba(255,255,255,0.07)", border: "1px solid var(--flow-border,#334155)", color: "var(--flow-text)", borderRadius: 6, padding: "5px 8px", fontSize: 11, outline: "none" }} />
                <div className="flex gap-1.5 flex-wrap">
                  {Array.isArray(outcomes) && outcomes.length ? outcomes.map((o, i) => (
                    <button key={o} className={`${dChip} ${i === 0 ? "wf-chip-primary" : "wf-chip"}`} disabled={!!busy} onClick={() => act(t, o)} data-wf-run-outcome={o}>
                      {busy === t.id + o ? <Loader2 size={11} className="animate-spin" /> : null}{o}
                    </button>
                  )) : (
                    <button className={`${dChip} wf-chip-primary`} disabled={!!busy} onClick={() => act(t, null)}>Mark done</button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
      {err ? <div className="text-xs" style={{ color: "#f87171" }}>{err}</div> : null}
    </div>
  );
}

// ── task card (My Work) ──────────────────────────────────────────────────────
function TaskCard({ t, me, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const outcomes = jsonish(t.outcomes, null);
  const fields = jsonish(t.form, []) || [];
  const complete = async (outcome) => {
    setBusy(outcome || "done"); setErr("");
    const fd = {}; const missing = [];
    fields.forEach((f) => { const v = form[f.key]; if (v == null || v === "") missing.push(f.label || f.key); else fd[f.key] = f.type === "number" ? Number(v) : v; });
    if (missing.length) { setBusy(""); setErr("Please fill: " + missing.join(", ")); return; }
    try {
      const p = { id: t.id, note: note.trim() || undefined };
      if (outcome) p.outcome = outcome;
      if (fields.length) p.form_data = fd;
      const r = await wfEngine("complete_task", p);
      setBusy(""); onDone(r);
    } catch (e) { setBusy(""); setErr(errMsg(e)); }
  };
  return (
    <div className={`${card} p-4`} data-wf-task={t.id}>
      <div className="flex items-center gap-2 flex-wrap cursor-pointer" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown size={14} className="text-page-muted" /> : <ChevronRight size={14} className="text-page-muted" />}
        {t.type === "decision" ? <GitBranch size={14} className="text-amber-500" /> : <ListChecks size={14} className="text-nav-active" />}
        <div className="font-medium text-sm text-page-text">{t.title}</div>
        <span className="text-xs text-page-muted">in <b>{t.definition_name}</b></span>
        <Assignee t={t} />
        <div className="flex-1" />
        <span className="text-[11px] text-page-muted">waiting since {fmtTime(t.created_at)}</span>
      </div>
      {open ? (
        <div className="mt-3 ml-6 space-y-3">
          {t.description ? <div className="text-sm text-page-muted whitespace-pre-wrap">{t.description}</div> : null}
          {t.subprocess ? (
            <div className="text-xs rounded-md px-2 py-1.5 inline-flex items-center gap-1.5" style={{ color: "#db2777", background: "#db27771a" }}>
              <Route size={12} />This step runs another workflow automatically — no action needed.
            </div>
          ) : t.endpoint ? (
            <div className="text-xs rounded-md px-2 py-1.5 inline-flex items-center gap-1.5" style={{ color: "#0891b2", background: "#0891b21a" }}>
              <Code2 size={12} />This step runs code automatically (endpoint <b>{t.endpoint}</b>) — no action needed.
            </div>
          ) : t.agent ? (
            <div className="text-xs rounded-md px-2 py-1.5 inline-flex items-center gap-1.5" style={{ color: "#8b5cf6", background: "#8b5cf61a" }}>
              <Bot size={12} />This step is handled automatically by the <b>{t.agent}</b> agent{t.claimed_by ? ` (picked up by ${t.claimed_by})` : " — waiting for the workflow-actors worker"}. You can still complete it manually below.
            </div>
          ) : null}
          <InstanceContext data={t.instance_data} />
          {fields.length ? (
            <div className="grid gap-2" style={{ maxWidth: 380 }}>
              {fields.map((f) => (
                <label key={f.key} className="text-sm">
                  <span className="block text-[11px] text-page-muted mb-1">{f.label || f.key}</span>
                  <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={`${inputSm} w-full h-8`} data-wf-field={f.key} />
                </label>
              ))}
            </div>
          ) : null}
          <label className="block text-sm" style={{ maxWidth: 380 }}>
            <span className="block text-[11px] text-page-muted mb-1">Comment (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} className={`${inputSm} w-full h-8`} placeholder="Add a note for the next step…" />
          </label>
          {err ? <div className="text-xs text-red-600">{err}</div> : null}
          <div className="flex items-center gap-2 flex-wrap">
            {Array.isArray(outcomes) && outcomes.length ? outcomes.map((o, i) => (
              <button key={o} className={i === 0 ? btnPrimary : btnGhost} disabled={!!busy} onClick={() => complete(o)} data-wf-outcome={o}>
                {busy === o ? <Loader2 size={13} className="animate-spin" /> : null}{o}
              </button>
            )) : (
              <button className={btnPrimary} disabled={!!busy} onClick={() => complete(null)} data-wf-complete="1">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}Mark done
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── My Work tab ──────────────────────────────────────────────────────────────
function MyWorkTab({ me, onCounts }) {
  const [sub, setSub] = useState("mine");
  const [tasks, setTasks] = useState(null);
  const [allTasks, setAllTasks] = useState(null);
  const [defs, setDefs] = useState(null);
  const [runs, setRuns] = useState(null);
  const [runDetail, setRunDetail] = useState({});
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");
  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };

  const load = useCallback(async () => {
    try {
      const [mine, all] = await Promise.all([
        wfEngine("my_tasks", {}),
        wfEngine("list_tasks", { status: "open", limit: 200 }),
      ]);
      setTasks(mine.tasks || []);
      setAllTasks((all.tasks || []).filter((t) => t.instance_status === "running"));
      onCounts?.((mine.tasks || []).length);
      setErr("");
    } catch (e) { setErr(errMsg(e)); setTasks((p) => p ?? []); setAllTasks((p) => p ?? []); }
  }, [onCounts]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (sub === "start" && defs === null) wfEngine("list_definitions", { status: "published" }).then((r) => setDefs(r.definitions || [])).catch(() => setDefs([]));
    if (sub === "runs") wfEngine("list_instances", { limit: 200 }).then((r) => setRuns((r.instances || []).filter((i) => i.started_by === me?.email))).catch(() => setRuns([]));
  }, [sub, defs, me?.email]);

  const onDone = (r) => { flash(r?.completed ? "Process completed 🎉" : "Step done — the process moved forward"); load(); };
  const myCount = (tasks || []).length;
  const subs = [["mine", `My tasks${myCount ? ` (${myCount})` : ""}`], ["all", "All open"], ["start", "Start a process"], ["runs", "My runs"]];

  return (
    <div className="space-y-4 max-w-3xl" data-tour="wf-mywork">
      <div className="flex items-center gap-1 flex-wrap">
        {subs.map(([k, label]) => (
          <button key={k} onClick={() => setSub(k)} data-wf-tab={k}
            className={`text-sm px-3 py-1.5 rounded-md border-0 cursor-pointer ${sub === k ? "bg-nav-active/10 text-nav-active font-medium" : "bg-transparent text-page-muted hover:text-page-text"}`}>
            {label}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-page-muted hover:text-nav-active bg-transparent border-0 cursor-pointer"><RefreshCw size={13} />Refresh</button>
      </div>
      {err ? <div className="text-sm text-red-600">{err}</div> : null}
      {sub === "mine" ? (
        tasks === null ? <div className="text-sm text-page-muted">Loading…</div> :
          !tasks.length ? (
            <div className={`${card} p-10 text-center space-y-1`}>
              <CheckCircle2 size={30} className="mx-auto text-emerald-500" />
              <div className="font-medium text-page-text">Nothing waiting on you</div>
              <div className="text-sm text-page-muted">When a process reaches a step assigned to you (or your role), it appears here.</div>
            </div>
          ) : <div className="space-y-3">{tasks.map((t) => <TaskCard key={t.id} t={t} me={me} onDone={onDone} />)}</div>
      ) : sub === "all" ? (
        allTasks === null ? <div className="text-sm text-page-muted">Loading…</div> :
          !allTasks.length ? <div className={`${card} p-6 text-center text-sm text-page-muted`}>No open steps anywhere — all processes are moving.</div> :
            <div className="space-y-3">{allTasks.map((t) => <TaskCard key={t.id} t={t} me={me} onDone={onDone} />)}</div>
      ) : sub === "start" ? (
        defs === null ? <div className="text-sm text-page-muted">Loading…</div> :
          !defs.length ? <div className={`${card} p-6 text-center text-sm text-page-muted`}>No published processes yet. Design one in the Processes tab.</div> :
            <div className="space-y-3">
              {defs.map((d) => (
                <div key={d.id} className={`${card} p-4 flex items-center gap-3 flex-wrap`} data-wf-start={d.id}>
                  <Rocket size={15} className="text-nav-active" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-page-text">{d.name}</div>
                    {d.description ? <div className="text-xs text-page-muted truncate" style={{ maxWidth: 480 }}>{d.description}</div> : null}
                  </div>
                  <div className="flex-1" />
                  <button className={btnPrimary} disabled={busyId === d.id} onClick={async () => {
                    setBusyId(d.id);
                    try { await wfEngine("start_process", { definition_id: d.id }); setBusyId(""); flash(`Started '${d.name}'`); load(); }
                    catch (e) { setBusyId(""); setErr(errMsg(e)); }
                  }}>
                    {busyId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}Start
                  </button>
                </div>
              ))}
            </div>
      ) : (
        runs === null ? <div className="text-sm text-page-muted">Loading…</div> :
          !runs.length ? <div className={`${card} p-6 text-center text-sm text-page-muted`}>You haven't started any runs yet.</div> :
            <div className="space-y-3">
              {runs.map((ins) => {
                const open = !!runDetail[ins.id];
                const d = runDetail[ins.id];
                return (
                  <div key={ins.id} className={`${card} p-4`} data-wf-run={ins.id}>
                    <div className="flex items-center gap-3 flex-wrap cursor-pointer" onClick={async () => {
                      if (open) { const nd = { ...runDetail }; delete nd[ins.id]; setRunDetail(nd); }
                      else { try { const r = await wfEngine("get_instance", { id: ins.id }); setRunDetail((p) => ({ ...p, [ins.id]: r })); } catch {} }
                    }}>
                      {open ? <ChevronDown size={14} className="text-page-muted" /> : <ChevronRight size={14} className="text-page-muted" />}
                      <div className="font-medium text-sm text-page-text">{ins.definition_name}</div>
                      <StatusBadge s={ins.status} />
                      <span className="text-xs text-page-muted">{fmtTime(ins.created_at)}</span>
                    </div>
                    {open && d ? (
                      <div className="mt-3 ml-6 space-y-1.5">
                        {(d.tasks || []).map((t) => (
                          <div key={t.id} className="flex items-center gap-2 text-xs text-page-text">
                            <StatusBadge s={t.status} />
                            <span className="font-medium">{t.title}</span>
                            <Assignee t={t} />
                            {t.outcome ? <span className="text-amber-600 font-medium">→ {t.outcome}</span> : null}
                            {t.completed_by ? <span className="text-page-muted">by {t.completed_by} · {fmtTime(t.completed_at)}</span> : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
      )}
      {toast ? <div className="fixed bottom-5 right-5 bg-page-text text-page-bg text-sm px-4 py-2 rounded-lg shadow-lg z-[70]">{toast}</div> : null}
    </div>
  );
}

// ── node / edge config panel (dark drawer INSIDE the studio canvas) ──────────
function FieldLabel({ children }) { return <span className="block text-[11px] text-page-muted mb-1">{children}</span>; }
function DLabel({ children }) { return <span className="block text-[11px] mb-1" style={{ color: "var(--flow-text-muted)" }}>{children}</span>; }
const dChip = "inline-flex items-center gap-1.5 rounded-md text-xs px-2.5 py-1.5 cursor-pointer";

function NodePanel({ graph, setGraph, sel, setSel, knownRoles, knownAgents, flowProjectId }) {
  const upNode = (id, patch) => setGraph({ ...graph, nodes: graph.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) });
  // Published definitions for the sub-process picker (composable workflows).
  const [pubDefs, setPubDefs] = useState([]);
  useEffect(() => {
    wfEngine("list_definitions", { status: "published" }).then((r) => setPubDefs(r.definitions || [])).catch(() => {});
  }, []);
  if (sel.kind === "edge") {
    const e = graph.edges.find((x) => wfEdgeId(x) === sel.id);
    if (!e) return null;
    const byId = Object.fromEntries(graph.nodes.map((n) => [n.id, n]));
    const src = byId[e.from];
    const replaceEdge = (patch) => {
      const ne = { ...e, ...patch }; delete ne.id; ne.id = wfEdgeId(ne);
      setGraph({ ...graph, edges: graph.edges.map((x) => (wfEdgeId(x) === sel.id ? ne : x)) });
      setSel({ kind: "edge", id: ne.id });
    };
    return (
      <div className="p-3 space-y-3">
        <div className="font-medium text-sm flex items-center gap-1.5"><ArrowRight size={14} />Link</div>
        <div className="text-xs" style={{ color: "var(--flow-text-muted)" }}>{src?.title || e.from} → {byId[e.to]?.title || e.to}</div>
        {src?.type === "decision" ? (
          <label className="block">
            <DLabel>Outcome (which decision result takes this path)</DLabel>
            <select value={e.outcome || ""} onChange={(ev) => replaceEdge({ outcome: ev.target.value })}>
              {(src.outcomes || []).filter(Boolean).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
        ) : null}
        <button className={`${dChip} wf-chip-danger`} onClick={() => { setGraph({ ...graph, edges: graph.edges.filter((x) => wfEdgeId(x) !== sel.id) }); setSel(null); }}>
          <Trash2 size={13} />Delete link
        </button>
      </div>
    );
  }
  const n = graph.nodes.find((x) => x.id === sel.id);
  if (!n) return null;
  const meta = WF_NODE_META[n.type] || WF_NODE_META.task;
  const form = Array.isArray(n.form) ? n.form : [];
  return (
    <div className="p-3 space-y-3">
      <div className="font-medium text-sm flex items-center gap-1.5" style={{ color: meta.color }}><meta.Icon size={14} />{meta.label}</div>
      {(n.type === "task" || n.type === "decision") ? (
        <>
          <label className="block"><DLabel>Title</DLabel>
            <input value={n.title || ""} onChange={(e) => upNode(n.id, { title: e.target.value })} data-tour="wf-node-title" /></label>
          <label className="block"><DLabel>Instructions (shown to whoever does this step)</DLabel>
            <textarea value={n.description || ""} onChange={(e) => upNode(n.id, { description: e.target.value })} rows={3} style={{ resize: "none" }} /></label>
          <label className="block"><DLabel>Role (anyone holding it can act)</DLabel>
            <input value={n.role || ""} onChange={(e) => upNode(n.id, { role: e.target.value })} list="wf-roles-list" placeholder="e.g. manager" />
            <datalist id="wf-roles-list">{knownRoles.map((r) => <option key={r} value={r} />)}</datalist></label>
          <label className="block"><DLabel>Or pin to a specific user (email, optional)</DLabel>
            <input value={n.assignee || ""} onChange={(e) => upNode(n.id, { assignee: e.target.value })} placeholder="user@company.com" /></label>
          <label className="block">
            <DLabel><span className="inline-flex items-center gap-1"><Bot size={11} style={{ color: "#b79df8" }} />Or assign to an AGENT (executed automatically)</span></DLabel>
            <select value={n.agent || ""} onChange={(e) => upNode(n.id, { agent: e.target.value || undefined })} data-tour="wf-agent-pick">
              <option value="">— no agent —</option>
              {knownAgents.map((a) => <option key={a.name} value={a.name}>{(a.icon ? a.icon + " " : "") + (a.display_name || a.name)}</option>)}
            </select>
            {n.agent ? <div className="text-[10px] mt-1" style={{ color: "#b79df8" }}>This step runs without human input: the <b>{n.agent}</b> agent picks it up and moves the process forward.</div> : null}
          </label>
          <label className="block">
            <DLabel><span className="inline-flex items-center gap-1"><Route size={11} style={{ color: "#f9a8d4" }} />Or run ANOTHER WORKFLOW (sub-process)</span></DLabel>
            <select value={n.subprocess || ""} data-tour="wf-subprocess-pick"
              onChange={(e) => {
                const d = pubDefs.find((x) => x.id === e.target.value);
                upNode(n.id, { subprocess: e.target.value || undefined, subprocess_name: d ? d.name : undefined });
              }}>
              <option value="">— no sub-process —</option>
              {pubDefs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {n.subprocess ? <div className="text-[10px] mt-1" style={{ color: "#f9a8d4" }}>Runs the <b>{n.subprocess_name || n.subprocess}</b> process as a child run (it sees this run's context); this step completes when the child run finishes and its results join the context.</div> : null}
          </label>
          <label className="block">
            <DLabel><span className="inline-flex items-center gap-1"><Code2 size={11} style={{ color: "#67e8f9" }} />Or run CODE (Flow endpoint name)</span></DLabel>
            <input value={n.endpoint || ""} onChange={(e) => upNode(n.id, { endpoint: e.target.value.trim() || undefined })}
              placeholder="e.g. cortex.myflow.transform" data-tour="wf-endpoint-name" />
            {n.endpoint ? (
              <div className="text-[10px] mt-1" style={{ color: "#67e8f9" }}>
                Runs the <b>{n.endpoint}</b> endpoint automatically when the process reaches this step; its result lands in the run context.
                {flowProjectId ? (
                  <a href={createPageUrl(`flow/projects/${flowProjectId}/endpoints/${encodeURIComponent(n.endpoint)}/flow`)}
                    target="_blank" rel="noreferrer" data-testid="wf-open-in-flow"
                    className="inline-flex items-center gap-1 ml-1.5 hover:underline" style={{ color: "#7ab3ff" }}>
                    Open the code in Flow <ExternalLink size={10} />
                  </a>
                ) : null}
              </div>
            ) : null}
          </label>
          {n.type === "decision" ? (
            <label className="block"><DLabel>Outcomes (comma-separated; each needs a link)</DLabel>
              <input value={(n.outcomes || []).join(", ")} onChange={(e) => upNode(n.id, { outcomes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="approve, reject" /></label>
          ) : null}
          <label className="flex items-center gap-2 text-xs" style={{ color: "var(--flow-text)" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={n.join === "all"} onChange={(e) => upNode(n.id, { join: e.target.checked ? "all" : undefined })} />
            Wait for ALL incoming branches (parallel join)
          </label>
          <div>
            <DLabel>Form fields (filled when completing the step)</DLabel>
            {form.map((f, i) => (
              <div key={i} className="flex items-center gap-1 mb-1">
                <input value={f.label || ""} placeholder="Label" style={{ flex: 1 }} onChange={(e) => {
                  const nf = form.slice(); nf[i] = { ...f, label: e.target.value, key: f.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_") }; upNode(n.id, { form: nf });
                }} />
                <select value={f.type || "text"} style={{ width: 74 }} onChange={(e) => { const nf = form.slice(); nf[i] = { ...f, type: e.target.value }; upNode(n.id, { form: nf }); }}>
                  <option value="text">text</option><option value="number">number</option><option value="date">date</option>
                </select>
                <button className="bg-transparent border-0 cursor-pointer" style={{ color: "var(--flow-text-muted)" }} onClick={() => upNode(n.id, { form: form.filter((_, j) => j !== i) })}><X size={13} /></button>
              </div>
            ))}
            <button className="text-xs bg-transparent border-0 cursor-pointer hover:underline" style={{ color: "#7ab3ff" }} onClick={() => upNode(n.id, { form: [...form, { key: "", label: "", type: "text" }] })}>+ add field</button>
          </div>
        </>
      ) : (
        <label className="block"><DLabel>Label</DLabel>
          <input value={n.title || ""} onChange={(e) => upNode(n.id, { title: e.target.value })} /></label>
      )}
      {n.type !== "start" ? (
        <button className={`${dChip} wf-chip-danger`} onClick={() => {
          setGraph({ ...graph, nodes: graph.nodes.filter((x) => x.id !== n.id), edges: graph.edges.filter((x) => x.from !== n.id && x.to !== n.id) });
          setSel(null);
        }}><Trash2 size={13} />Delete node</button>
      ) : null}
    </div>
  );
}

// ── triggers tab (event listeners) ───────────────────────────────────────────
// ── Triggers tab: kind-aware cards + editors for EVERY listener kind ────────
// Triggers are pure config rows (wf_listener.kind + config) — this tab is the
// generic editor over them: schedule (cadence), data_condition (SQL over the
// governed DB with a live "Test now" preview), email (mailbox query profile),
// manual (upload form spec). Everything drives the same upsert_listener.
const TRIGGER_KINDS = [
  { kind: "data_condition", label: "Data condition", icon: Zap, color: "#a3e635",
    blurb: "Fires when a SELECT over the project's governed database returns rows (evaluated ~every minute; one run per distinct dedup value)." },
  { kind: "schedule", label: "Schedule", icon: Clock, color: "#f59e0b",
    blurb: "Fires once per period — hourly, daily, weekly or monthly." },
  { kind: "email", label: "Email", icon: Mail, color: "#ec4899",
    blurb: "Watches the connected mailbox; every new matching message starts a run with the email in the run context." },
  { kind: "manual", label: "Upload form", icon: Upload, color: "#22d3ee",
    blurb: "An in-product form: the uploaded document becomes the run's event. Use ONLY when the process genuinely consumes a user document." },
];
const kindMetaOf = (k) => TRIGGER_KINDS.find((t) => t.kind === k) || { label: k, icon: Zap, color: "#a3e635" };

// Per-kind config fields — one generic editor renders them all.
function listenerFields(kind) {
  if (kind === "schedule") return [
    ["label", "Label", "input", "Month-end"],
    ["every", "Cadence", "select", ["hourly", "daily", "weekly", "monthly"]],
    ["description", "Description (shown on the trigger)", "textarea", "What this schedule kicks off…"],
  ];
  if (kind === "data_condition") return [
    ["label", "Label", "input", "Telemetry anomaly"],
    ["sql", "Condition SQL — SELECT that returns rows when the trigger should fire", "sql", "SELECT … FROM … WHERE …"],
    ["dedup_column", "Dedup column (one run per distinct value of this result column)", "input", "asset_id"],
    ["description", "Description (shown on the trigger)", "textarea", "When this fires and why…"],
  ];
  if (kind === "email") return [
    ["query", "Mailbox search query (Gmail syntax)", "input", "subject:(application OR resume)"],
    ["position_title", "Position / profile title", "input", ""],
    ["requirements", "Requirements (what the screening agent scores against)", "textarea", ""],
    ["threshold", "Fit threshold (0-100)", "input", "70"],
    ["declined_label", "Declined emails label", "input", ""],
    ["processed_label", "Processed emails label", "input", ""],
  ];
  return [ // manual upload form
    ["form.title", "Form title", "input", "Start the process"],
    ["form.document", "Document noun (\u201cUpload the <noun>\u201d)", "input", "brief"],
    ["form.hint", "Hint", "textarea", "Upload the … to kick off …"],
    ["form.accept", "Accepted extensions (comma-separated)", "input", ".docx,.txt,.md,.csv"],
    ["label", "Label (canvas node)", "input", ""],
  ];
}
const cfgGet = (cfg, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), cfg);
const cfgSet = (cfg, path, v) => {
  const parts = path.split("."); const out = { ...(cfg || {}) }; let cur = out;
  parts.slice(0, -1).forEach((k) => { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; });
  cur[parts[parts.length - 1]] = v; return out;
};

function ListenerEditor({ kind, cfg, onChange, onTest, testResult, testing }) {
  return (
    <div className="space-y-2.5">
      {listenerFields(kind).map(([path, label, type, ph]) => {
        const raw = cfgGet(cfg, path);
        const val = path === "form.accept" && Array.isArray(raw) ? raw.join(",") : (raw ?? "");
        const set = (v) => onChange(cfgSet(cfg, path, path === "form.accept" ? String(v).split(",").map((s) => s.trim()).filter(Boolean) : v));
        return (
          <label key={path} className="block text-sm">
            <FieldLabel>{label}</FieldLabel>
            {type === "select" ? (
              <select value={val || ph[0]} onChange={(e) => set(e.target.value)} className={`${inputSm} w-44 h-8`} data-wf-trigfield={path}>
                {ph.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : type === "textarea" || type === "sql" ? (
              <textarea value={val} onChange={(e) => set(e.target.value)} rows={type === "sql" ? 4 : 2}
                className={`${inputSm} w-full resize-y text-xs ${type === "sql" ? "font-mono" : ""}`} placeholder={ph} data-wf-trigfield={path} />
            ) : (
              <input value={val} onChange={(e) => set(e.target.value)} className={`${inputSm} w-full h-8`} placeholder={ph} data-wf-trigfield={path} />
            )}
          </label>
        );
      })}
      {kind === "data_condition" ? (
        <div className="space-y-1.5">
          <button className={btnGhost} disabled={testing || !String(cfg?.sql || "").trim()} onClick={onTest} data-wf-test-condition>
            {testing ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}Test now
          </button>
          {testResult ? (
            <div className={`text-xs rounded-md border px-2.5 py-2 ${testResult.error ? "border-red-400/40 text-red-500" : "border-card-border text-page-muted"}`} data-wf-test-result>
              {testResult.error ? testResult.error :
                testResult.rows.length === 0 ? "No rows match right now — the trigger would NOT fire." : (
                  <>
                    <b className="text-page-text">{testResult.rows.length}{testResult.rows.length >= 50 ? "+" : ""} matching row{testResult.rows.length === 1 ? "" : "s"}</b> — the trigger would fire. First match:{" "}
                    <code className="bg-card-border/30 px-1 rounded break-all">{JSON.stringify(testResult.rows[0]).slice(0, 220)}</code>
                  </>
                )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ListenerCard({ l, onChanged, onError }) {
  const meta = kindMetaOf(l.kind);
  const [cfg, setCfg] = useState(() => jsonish(l.config, {}) || {});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await wfEngine("upsert_listener", { id: l.id, kind: l.kind, config: cfg }); setDirty(false); setBusy(false); onChanged(); }
    catch (e) { setBusy(false); onError(errMsg(e)); }
  };
  const runTest = async () => {
    setTesting(true); setTest(null);
    try { const r = await wfEngine("query_data", { sql: cfg.sql }); setTest({ rows: r.rows || [] }); }
    catch (e) { setTest({ error: errMsg(e) }); }
    setTesting(false);
  };
  const summary = l.kind === "schedule" ? `every ${cfg.every || "?"}`
    : l.kind === "data_condition" ? (cfg.sql ? String(cfg.sql).replace(/\s+/g, " ").slice(0, 90) + "…" : "no SQL yet")
    : l.kind === "email" ? (cfg.query || "no query")
    : (cfg.form?.title || "upload form");
  return (
    <div className={`${card} p-4 space-y-2`} data-wf-listener={l.id} data-wf-listener-kind={l.kind}>
      <div className="flex items-center gap-2 flex-wrap">
        <meta.icon size={14} style={{ color: meta.color }} />
        <span className="font-medium text-sm text-page-text">{cfg.label || meta.label}</span>
        <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${l.enabled ? "bg-emerald-500/15 text-emerald-600" : "bg-slate-400/15 text-slate-500"}`}>{l.enabled ? "enabled" : "disabled"}</span>
        <div className="flex-1" />
        <button className={btnGhost + " text-xs"} onClick={() => setOpen((v) => !v)} data-wf-edit-trigger>{open ? "Close" : "Edit"}</button>
        <button className={btnDanger} style={{ color: "inherit" }} onClick={() => wfEngine("upsert_listener", { id: l.id, enabled: !l.enabled }).then(onChanged).catch((e) => onError(errMsg(e)))}>{l.enabled ? "Disable" : "Enable"}</button>
        <button className="text-page-muted hover:text-red-600 bg-transparent border-0 cursor-pointer" title="Delete" onClick={() => wfEngine("delete_listener", { id: l.id }).then(onChanged).catch((e) => onError(errMsg(e)))}><Trash2 size={13} /></button>
      </div>
      {!open ? (
        <>
          <div className="text-xs text-page-muted"><code className="bg-card-border/30 px-1 rounded break-all">{summary}</code></div>
          {cfg.description ? <div className="text-xs text-page-muted">{String(cfg.description).slice(0, 220)}</div> : null}
          <div className="text-[11px] text-page-muted">{l.last_checked_at ? `last checked ${fmtTime(l.last_checked_at)}` : "not checked yet"}</div>
        </>
      ) : (
        <div className="pt-1 space-y-2.5">
          <ListenerEditor kind={l.kind} cfg={cfg} onChange={(c) => { setCfg(c); setDirty(true); }} onTest={runTest} testResult={test} testing={testing} />
          <div className="flex justify-end">
            <button className={btnPrimary} disabled={busy || !dirty} onClick={save} data-wf-save-trigger>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}Save trigger
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TriggersTab({ definitionId }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [newKind, setNewKind] = useState("data_condition");
  const [newCfg, setNewCfg] = useState({});
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const load = useCallback(() => {
    wfEngine("list_listeners", { definition_id: definitionId }).then((r) => { setList(r.listeners || []); setErr(""); }).catch((e) => { setErr(errMsg(e)); setList([]); });
  }, [definitionId]);
  useEffect(load, [load]);
  const runTest = async () => {
    setTesting(true); setTest(null);
    try { const r = await wfEngine("query_data", { sql: newCfg.sql }); setTest({ rows: r.rows || [] }); }
    catch (e) { setTest({ error: errMsg(e) }); }
    setTesting(false);
  };
  const create = async () => {
    setBusy(true); setErr("");
    try {
      await wfEngine("upsert_listener", { definition_id: definitionId, kind: newKind, config: newCfg });
      setBusy(false); setNewCfg({}); setTest(null); load();
    } catch (e) { setBusy(false); setErr(errMsg(e)); }
  };
  const canCreate = newKind === "data_condition" ? !!String(newCfg.sql || "").trim()
    : newKind === "email" ? !!String(newCfg.query || "").trim() : true;
  return (
    <div className="max-w-2xl space-y-4" data-tour="wf-triggers">
      <div className="text-sm text-page-muted">
        Triggers start this process automatically — a <b>data condition</b> over the governed database, a <b>schedule</b>, an incoming <b>email</b>,
        or (only when the process consumes a user document) an <b>upload form</b>. Every trigger can also be run on demand from the canvas.
      </div>
      {list === null ? <div className="text-sm text-page-muted">Loading…</div> :
        list.map((l) => <ListenerCard key={l.id + String(l.updated_at || "")} l={l} onChanged={load} onError={setErr} />)}
      <div className={`${card} p-4 space-y-3`} data-wf-new-trigger>
        <div className="font-medium text-sm text-page-text flex items-center gap-1.5"><Plus size={14} className="text-nav-active" />New trigger</div>
        <div className="flex gap-1.5 flex-wrap">
          {TRIGGER_KINDS.map((t) => (
            <button key={t.kind} onClick={() => { setNewKind(t.kind); setNewCfg({}); setTest(null); }} data-wf-newkind={t.kind}
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border cursor-pointer ${newKind === t.kind ? "border-nav-active bg-nav-active/10 text-nav-active font-medium" : "border-card-border bg-transparent text-page-text hover:bg-card-border/30"}`}>
              <t.icon size={13} />{t.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-page-muted">{kindMetaOf(newKind).blurb}</div>
        <ListenerEditor kind={newKind} cfg={newCfg} onChange={setNewCfg} onTest={runTest} testResult={test} testing={testing} />
        {err ? <div className="text-xs text-red-600">{err}</div> : null}
        <button className={btnPrimary} disabled={busy || !canCreate} onClick={create} data-tour="wf-add-trigger">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}Add trigger
        </button>
      </div>
    </div>
  );
}

// ── runs tab ─────────────────────────────────────────────────────────────────
function InstanceCard({ ins, onChanged, onWatch }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const log = jsonish(ins.log, []) || [];
  const last = log.length ? log[log.length - 1] : null;
  return (
    <div className={`${card} p-4`} data-wf-instance={ins.id}>
      <div className="flex items-center gap-3 flex-wrap cursor-pointer" onClick={async () => {
        if (!open && !detail) { try { setDetail(await wfEngine("get_instance", { id: ins.id })); } catch {} }
        setOpen(!open);
      }}>
        {open ? <ChevronDown size={14} className="text-page-muted" /> : <ChevronRight size={14} className="text-page-muted" />}
        <div className="font-medium text-sm text-page-text">{ins.definition_name}</div>
        <StatusBadge s={ins.status} />
        <span className="text-xs text-page-muted">started by {ins.started_by} · {fmtTime(ins.created_at)}</span>
        <div className="flex-1" />
        {onWatch ? (
          <button className={btnGhost} style={{ padding: "3px 10px", fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); onWatch(ins.id); }} data-wf-watch={ins.id}>
            <Radio size={12} />Watch
          </button>
        ) : null}
        {ins.status === "running" ? (
          <button className={btnDanger} onClick={(e) => { e.stopPropagation(); wfEngine("cancel_instance", { id: ins.id }).then(onChanged).catch(() => {}); }}>Cancel</button>
        ) : null}
      </div>
      {!open && last ? <div className="mt-1.5 ml-6 text-xs text-page-muted">{String(last.msg || "")} — {last.by} · {fmtTime(last.at)}</div> : null}
      {open ? (
        detail === null ? <div className="mt-2 ml-6 text-xs text-page-muted">Loading…</div> : (
          <div className="mt-3 ml-6 space-y-2">
            {(detail.tasks || []).map((t) => (
              <div key={t.id} className="flex items-center gap-2 text-xs text-page-text">
                <StatusBadge s={t.status} />
                <span className="font-medium">{t.title}</span>
                <Assignee t={t} />
                {t.outcome ? <span className="text-amber-600 font-medium">→ {t.outcome}</span> : null}
                {t.completed_by ? <span className="text-page-muted">by {t.completed_by} · {fmtTime(t.completed_at)}</span> : null}
              </div>
            ))}
            <div className="pt-1 border-t border-card-border space-y-0.5">
              {(jsonish(detail.instance?.log, []) || []).map((l, i) => (
                <div key={i} className="text-[11px] text-page-muted">{fmtTime(l.at)} · {l.by} — {l.msg}</div>
              ))}
            </div>
          </div>
        )
      ) : null}
    </div>
  );
}

function RunsTab({ definitionId, onWatch }) {
  const [list, setList] = useState(null);
  const load = useCallback(() => {
    wfEngine("list_instances", definitionId ? { definition_id: definitionId } : {}).then((r) => setList(r.instances || [])).catch(() => setList([]));
  }, [definitionId]);
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);
  if (list === null) return <div className="text-sm text-page-muted">Loading…</div>;
  if (!list.length) return <div className={`${card} p-6 text-center text-sm text-page-muted max-w-2xl`}>No runs yet.</div>;
  return <div className="space-y-2 max-w-3xl">{list.map((i) => <InstanceCard key={i.id} ins={i} onChanged={load} onWatch={onWatch} />)}</div>;
}

// ── roles tab ────────────────────────────────────────────────────────────────
function RolesTab() {
  const [roles, setRoles] = useState(null);
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const load = () => wfEngine("list_roles", {}).then((r) => setRoles(r.roles || [])).catch((e) => { setErr(errMsg(e)); setRoles([]); });
  useEffect(() => { load(); }, []);
  const grouped = {};
  (roles || []).forEach((r) => { (grouped[r.role] = grouped[r.role] || []).push(r); });
  return (
    <div className="max-w-2xl space-y-4" data-tour="wf-roles">
      <div className="text-sm text-page-muted">People holding a role see (and can complete) every open step assigned to that role. A role with no members is open to everyone.</div>
      <div className="flex gap-2 items-end flex-wrap">
        <label className="text-sm"><FieldLabel>Role</FieldLabel><input value={role} onChange={(e) => setRole(e.target.value)} placeholder="manager" className={`${inputSm} h-8 w-40`} /></label>
        <label className="text-sm"><FieldLabel>Member email</FieldLabel><input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@company.com" className={`${inputSm} h-8 w-64`} /></label>
        <button className={btnPrimary} disabled={!role.trim() || !email.trim()} onClick={() => {
          wfEngine("assign_role", { role: role.trim(), email: email.trim() }).then(() => { setEmail(""); load(); }).catch((e) => setErr(errMsg(e)));
        }}><Plus size={14} />Add</button>
      </div>
      {err ? <div className="text-sm text-red-600">{err}</div> : null}
      {roles === null ? <div className="text-sm text-page-muted">Loading…</div> :
        !Object.keys(grouped).length ? <div className={`${card} p-6 text-center text-sm text-page-muted`}>No roles yet. Add the first membership above.</div> :
          Object.keys(grouped).sort().map((rn) => (
            <div key={rn} className={`${card} p-4`}>
              <div className="font-medium text-sm text-page-text flex items-center gap-2"><Users size={14} className="text-nav-active" />{rn}</div>
              <div className="mt-2 space-y-1">
                {grouped[rn].map((m) => (
                  <div key={m.user_email} className="flex items-center gap-2 text-sm text-page-muted">
                    <span>{m.user_email}</span>
                    <button className="text-page-muted hover:text-red-600 bg-transparent border-0 cursor-pointer" title="Remove"
                      onClick={() => wfEngine("remove_role", { role: rn, email: m.user_email }).then(load)}><X size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
    </div>
  );
}

// ── process editor (canvas + panel + triggers + runs) ────────────────────────
const NEW_GRAPH = { nodes: [{ id: "start", type: "start", title: "Start", x: 60, y: 200 }, { id: "end", type: "end", title: "Done", x: 640, y: 200 }], edges: [] };

function Editor({ def, onBack, onSaved }) {
  const [name, setName] = useState(def.name);
  const [graph, setGraphRaw] = useState(() => {
    const g = jsonish(def.graph, null);
    return g?.nodes?.length ? g : JSON.parse(JSON.stringify(NEW_GRAPH));
  });
  const [sel, setSel] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  // Composition map: all definitions (graphs included) so sub-process
  // references render as edges between processes.
  const [showMap, setShowMap] = useState(false);
  const [mapDefs, setMapDefs] = useState(null);
  useEffect(() => {
    if (!showMap || mapDefs) return;
    wfEngine("list_definitions", {}).then((r) => setMapDefs(r.definitions || [])).catch(() => setMapDefs([]));
  }, [showMap, mapDefs]);
  const [msg, setMsg] = useState("");
  const [problems, setProblems] = useState(null);
  const [status, setStatus] = useState(def.status);
  const [tab, setTab] = useState("design");
  const [knownRoles, setKnownRoles] = useState([]);
  const [knownAgents, setKnownAgents] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [runModal, setRunModal] = useState(null);   // the manual listener the form runs through
  const [runId, setRunId] = useState(null);         // live-run mode when set
  const [run, setRun] = useState(null);             // get_instance payload
  const [flowProjectId, setFlowProjectId] = useState(null); // for "Open in Flow" links on code steps
  useEffect(() => {
    wfEngine("list_roles", {}).then((r) => setKnownRoles([...new Set((r.roles || []).map((x) => x.role))])).catch(() => {});
    wfEngine("list_agents", {}).then((r) => setKnownAgents(r.agents || [])).catch(() => {});
    listViews().then((r) => setFlowProjectId(r?.flow_project_id || null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab !== "design") return;
    wfEngine("list_listeners", { definition_id: def.id }).then((r) => setTriggers(r.listeners || [])).catch(() => {});
  }, [tab, def.id]);
  // Live run polling: the canvas animates from the instance's task states and
  // fired edges; keep polling briefly after completion so the last transition
  // renders, then stop.
  useEffect(() => {
    if (!runId) { setRun(null); return; }
    let stop = false;
    const tick = async () => {
      try {
        const r = await wfEngine("get_instance", { id: runId });
        if (!stop) setRun(r);
        if (r?.instance?.status !== "running") return true;
      } catch { /* transient */ }
      return false;
    };
    tick();
    const iv = setInterval(async () => { if (await tick()) clearInterval(iv); }, 2500);
    return () => { stop = true; clearInterval(iv); };
  }, [runId]);
  const runState = useMemo(() => {
    if (!runId || !run?.instance) return null;
    const nodes = {};
    const tasks = {};
    (graph.nodes || []).forEach((n) => { if (n.type === "start") nodes[n.id] = "done"; });
    (run.tasks || []).forEach((t) => {
      nodes[t.node_id] = t.status === "open" ? "active" : t.status === "done" ? "done" : "skipped";
      tasks[t.node_id] = t;
    });
    const fired = {};
    Object.keys(jsonish(run.instance.fired, {}) || {}).forEach((k) => { fired[k] = true; });
    (graph.nodes || []).forEach((n) => {
      if (n.type === "end" && (graph.edges || []).some((e) => e.to === n.id && fired[wfEdgeId(e)])) nodes[n.id] = "done";
    });
    return { nodes, tasks, fired, running: run.instance.status === "running" };
  }, [runId, run, graph]);
  const setGraph = (g) => { setGraphRaw(g); setDirty(true); };
  const manualTrigger = triggers.find((l) => l.enabled !== false && (l.kind === "manual" || (jsonish(l.config, {}) || {}).form)) || null;
  // Any trigger opens the run modal — it shows the upload form only for
  // manual+form listeners; schedule/data_condition/form-less manual get the
  // one-click "Run now" view (email too — a manual start is always possible).
  const onTriggerClick = (l) => setRunModal(l);

  const addNode = (type) => {
    const id = "n" + Date.now().toString(36);
    const count = graph.nodes.length;
    // "code" is not a distinct graph type: it is a task step whose `endpoint`
    // names a Flow endpoint that executes automatically (mirrors how agent
    // steps are task nodes with `agent` set).
    const isCode = type === "code";
    const isProc = type === "process";
    const nodeType = isCode || isProc ? "task" : type;
    const n = { id, type: nodeType, title: type === "decision" ? "New decision" : type === "end" ? "End" : isCode ? "Run code" : isProc ? "Run process" : "New step", x: 240 + (count % 5) * 60, y: 80 + (count % 7) * 50 };
    if (isCode) n.endpoint = "";
    if (isProc) n.subprocess = "";
    if (type === "decision") n.outcomes = ["approve", "reject"];
    setGraph({ ...graph, nodes: [...graph.nodes, n] });
    setSel({ kind: "node", id });
  };
  const save = async (thenPublish) => {
    setBusy(thenPublish ? "publish" : "save"); setMsg("");
    try {
      const r = await wfEngine("save_definition", { id: def.id, name, graph });
      setProblems(r.validation || []);
      setDirty(false); setStatus("draft");
      if (thenPublish) {
        if ((r.validation || []).length) { setBusy(""); setMsg("Fix the issues below before publishing."); return; }
        await wfEngine("publish_definition", { id: def.id });
        setStatus("published"); setMsg("Published — people can now start this process.");
      } else setMsg("Saved.");
      setBusy(""); onSaved();
    } catch (e) { setBusy(""); setMsg(errMsg(e)); }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-tour="wf-editor">
      <div className="flex items-center gap-2 flex-wrap px-4 pt-3 pb-2">
        <button onClick={onBack} className="text-page-muted hover:text-page-text text-sm bg-transparent border-0 cursor-pointer">← All processes</button>
        <PencilLine size={14} className="text-page-muted ml-1" />
        <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} className={`${inputSm} h-8 w-72 font-medium`} data-tour="wf-name" />
        <StatusBadge s={status} />
        {dirty ? <span className="text-[11px] text-amber-500">unsaved changes</span> : null}
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          {[["design", "Design"], ["triggers", "Triggers"], ["runs", "Runs"], ["history", "History"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`text-sm px-3 py-1.5 rounded-md border-0 cursor-pointer ${tab === k ? "bg-nav-active/20 text-nav-active font-medium" : "bg-transparent text-page-muted hover:text-page-text"}`}>{label}</button>
          ))}
        </div>
        {msg ? <span className="text-xs text-page-muted ml-2">{msg}</span> : null}
      </div>
      {tab === "design" ? (
        // The studio: the SAME full-bleed dark canvas as the Pipeline page —
        // flow-studio-root dark activates the theme-cortex variables; every
        // control floats INSIDE the canvas (palette top-left, actions
        // top-right, stats chip, config drawer right, validation bottom).
        // Edge-to-edge: no rounding, no border, no white frame.
        <div className="wf-studio flow-studio-root dark relative flex-1 min-h-0 overflow-hidden" data-tour="wf-canvas">
          <style>{`
            .wf-studio .react-flow__handle { width: 11px; height: 11px; border-radius: 11px; }
            .wf-studio .react-flow__handle-left { left: -6px; }
            .wf-studio .react-flow__handle-right { right: -6px; }
            .wf-studio .wf-panel input, .wf-studio .wf-panel select, .wf-studio .wf-panel textarea {
              width: 100%; background: rgba(255,255,255,0.07); border: 1px solid var(--flow-border, #334155);
              color: var(--flow-text); border-radius: 6px; padding: 6px 8px; font-size: 12px; outline: none; }
            .wf-studio .wf-panel input:focus, .wf-studio .wf-panel select:focus, .wf-studio .wf-panel textarea:focus { border-color: #6366f1; }
            .wf-studio .wf-panel select option { background: var(--flow-node-bg); color: var(--flow-text); }
            .wf-studio .wf-chip { background: rgba(255,255,255,0.08); border: 1px solid var(--flow-border, #334155); color: var(--flow-text); }
            .wf-studio .wf-chip:hover { background: rgba(255,255,255,0.14); }
            .wf-studio .wf-chip-primary { background: #2563eb; border: 1px solid #2563eb; color: #fff; }
            .wf-studio .wf-chip-primary:hover { background: #1d4ed8; }
            .wf-studio .wf-chip-danger { background: rgba(239,68,68,0.12); border: 1px solid rgba(239,68,68,0.4); color: #fca5a5; }
            .wf-studio .wf-chip:disabled, .wf-studio .wf-chip-primary:disabled { opacity: .5; cursor: default; }
            .wf-studio .wf-pulse { animation: wfpulse 1.5s ease-in-out infinite; }
            .wf-studio .wf-fx { animation: wffx .45s cubic-bezier(.34,1.56,.64,1) both; }
            @keyframes wffx { from { opacity: 0; transform: translateY(4px) scale(.7); } to { opacity: 1; transform: none; } }
            .wf-studio .wf-status { animation: wfstatus .3s ease-out both; }
            @keyframes wfstatus { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: none; } }
            .wf-studio .wf-dots::after { content: ''; animation: wfdots 1.4s steps(4, end) infinite; }
            @keyframes wfdots { 0% { content: ''; } 25% { content: '.'; } 50% { content: '..'; } 75% { content: '...'; } }
            @keyframes wfpulse {
              0%, 100% { box-shadow: 0 0 0 2px rgba(96,165,250,.75), 0 0 16px rgba(96,165,250,.45); }
              50% { box-shadow: 0 0 0 5px rgba(96,165,250,.95), 0 0 30px rgba(96,165,250,.85); }
            }
          `}</style>
          {showMap ? (
            <CompositionMap definitions={mapDefs || []} currentId={def.id}
              onOpen={(id) => { window.location.href = createPageUrl("workflows") + `?tab=processes&def=${encodeURIComponent(id)}`; }}
              onClose={() => setShowMap(false)} />
          ) : null}
          <WorkflowCanvas canvasKey={def.id} graph={graph} onGraphChange={setGraph} sel={sel} onSelect={setSel}
            readOnly={!!runId} runState={runState} triggers={triggers} onTriggerClick={onTriggerClick} />

          {/* palette — top-left (hidden while watching a run) */}
          {!runId ? (
            <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5">
              <span className="text-[11px] px-1" style={{ color: "var(--flow-text-muted)" }}>Add:</span>
              <button className={`${dChip} wf-chip`} onClick={() => addNode("task")} data-tour="wf-add-step"><Square size={13} style={{ color: WF_NODE_META.task.color }} />Step</button>
              <button className={`${dChip} wf-chip`} onClick={() => addNode("decision")}><GitBranch size={13} style={{ color: WF_NODE_META.decision.color }} />Decision</button>
              <button className={`${dChip} wf-chip`} onClick={() => addNode("code")} data-tour="wf-add-code"><Code2 size={13} style={{ color: "#67e8f9" }} />Code</button>
              <button className={`${dChip} wf-chip`} onClick={() => addNode("process")} data-tour="wf-add-subprocess"><Route size={13} style={{ color: "#f9a8d4" }} />Sub-process</button>
              <button className={`${dChip} wf-chip`} onClick={() => setShowMap(true)} data-testid="wf-composition-map-btn" title="How this project's processes compose (which runs which)"><GitBranch size={13} style={{ color: "#a5b4fc" }} />Map</button>
              <button className={`${dChip} wf-chip`} onClick={() => addNode("end")}><Flag size={13} style={{ color: "#94a3b8" }} />End</button>
            </div>
          ) : (
            <div className="absolute top-3 left-3 z-20 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs"
              style={{ background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.5)", color: "#93c5fd" }}>
              <Radio size={12} className={run?.instance?.status === "running" ? "animate-pulse" : ""} />
              {run?.instance?.status === "running" ? "Live run — agents and people are moving this process" : "Run finished"}
            </div>
          )}

          {/* actions — top-right, like Refresh / Add target / Run pipeline */}
          <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5">
            {!runId ? (
              <>
                <button className={`${dChip} wf-chip`} disabled={!!busy} onClick={() => save(false)} data-tour="wf-save">
                  {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}Save</button>
                <button className={`${dChip} wf-chip`} disabled={!!busy} onClick={() => save(true)} data-tour="wf-publish">
                  {busy === "publish" ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}Publish</button>
                {status === "published" && !dirty ? (
                  <button className={`${dChip} wf-chip-primary`} disabled={!!busy} data-tour="wf-run" onClick={async () => {
                    if (manualTrigger) { setRunModal(manualTrigger); return; }
                    setBusy("start");
                    try { const r = await wfEngine("start_process", { definition_id: def.id }); setBusy(""); setRunId(r.instance.id); }
                    catch (e) { setBusy(""); setMsg(errMsg(e)); }
                  }}>{busy === "start" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}Run</button>
                ) : null}
              </>
            ) : (
              <button className={`${dChip} wf-chip`} onClick={() => { setRunId(null); }} data-tour="wf-exit-run"><X size={13} />Exit run view</button>
            )}
          </div>
          {/* stats chip under the actions, pipeline-style */}
          <div className="absolute z-20 right-3 text-[11px] rounded px-2 py-1 pointer-events-none"
            style={{ top: 52, color: "var(--flow-text-muted)", background: "rgba(255,255,255,0.06)" }}>
            {(graph.nodes || []).length} nodes · {(graph.edges || []).length} links
            {problems?.length ? <span style={{ color: "#fbbf24" }}> · {problems.length} issue{problems.length > 1 ? "s" : ""}</span>
              : problems ? <span style={{ color: "#34d399" }}> · valid</span> : null}
            {dirty ? <span style={{ color: "#fbbf24" }}> · unsaved</span> : null}
          </div>

          {/* right drawer: run output while watching, otherwise the config panel */}
          {runId ? (
            <div className="wf-panel absolute top-3 bottom-3 right-3 z-30 w-80 rounded-lg overflow-y-auto shadow-xl"
              style={{ background: "var(--flow-node-bg)", border: "1px solid var(--flow-border, #334155)", color: "var(--flow-text)" }}>
              {run ? <RunPanel run={run} graph={graph}
                onAct={async () => { try { setRun(await wfEngine("get_instance", { id: runId })); } catch {} }}
                onClose={() => setRunId(null)} />
                : <div className="p-3 text-xs" style={{ color: "var(--flow-text-muted)" }}>Starting run…</div>}
            </div>
          ) : sel ? (
            <div className="wf-panel absolute top-3 bottom-3 right-3 z-30 w-72 rounded-lg overflow-y-auto shadow-xl"
              style={{ background: "var(--flow-node-bg)", border: "1px solid var(--flow-border, #334155)", color: "var(--flow-text)" }} data-tour="wf-panel">
              <div className="flex justify-end px-2 pt-2 -mb-6">
                <button className="bg-transparent border-0 cursor-pointer" style={{ color: "var(--flow-text-muted)" }} onClick={() => setSel(null)}><X size={14} /></button>
              </div>
              <NodePanel graph={graph} setGraph={setGraph} sel={sel} setSel={setSel} knownRoles={knownRoles} knownAgents={knownAgents} flowProjectId={flowProjectId} />
            </div>
          ) : (
            <div className="absolute bottom-3 right-3 z-20 rounded-lg px-3 py-2 text-[11px] max-w-[280px] pointer-events-none"
              style={{ background: "rgba(255,255,255,0.06)", color: "var(--flow-text-muted)" }}>
              Click the <span style={{ color: "#22d3ee" }}>Resume upload</span> trigger to run with a candidate · click a node or link to edit · scroll to zoom, drag to pan
            </div>
          )}

          {/* validation problems — bottom-center overlay banner */}
          {problems?.length ? (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 rounded-lg px-3 py-2 max-w-[60%]"
              style={{ background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.5)" }}>
              {problems.map((p, i) => <div key={i} className="text-xs" style={{ color: "#fcd34d" }}>• {p}</div>)}
            </div>
          ) : null}
        </div>
      ) : tab === "triggers" ? <div className="overflow-y-auto flex-1 px-6 pb-6 pt-2"><TriggersTab definitionId={def.id} /></div>
        : tab === "history" ? (
          // A process is pg rows, not a fork endpoint, so its history comes from
          // the engine's own append-only version table rather than Flow's git —
          // same panel, different store (see components/VersionHistory).
          <div className="overflow-y-auto flex-1 px-6 pb-6 pt-2">
            <VersionHistory kind="workflow" name={def.id} title="Process history"
                            canRestore onRestored={() => window.location.reload()} />
          </div>
        )
        : <div className="overflow-y-auto flex-1 px-6 pb-6 pt-2"><RunsTab definitionId={def.id} onWatch={(id) => { setRunId(id); setTab("design"); }} /></div>}
      {runModal ? (
        <RunProcessModal listener={runModal} onClose={() => setRunModal(null)}
          onStarted={(id) => { setRunModal(null); setSel(null); setRunId(id); setTab("design"); }}
          onEdit={() => { setRunModal(null); setTab("triggers"); }} />
      ) : null}
    </div>
  );
}

// Full-bleed editor screen — replaces the padded light page while a process is
// open, exactly like the Pipeline page owns its whole content area. The `dark`
// class flips every cortex token (page/card/text vars), so the header, inputs,
// tabs and the Triggers/Runs cards all render dark without bespoke styling.
function EditorScreen({ defId, onBack }) {
  const [def, setDef] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let gone = false;
    wfEngine("get_definition", { id: defId })
      .then((r) => { if (!gone) setDef(r.definition); })
      .catch((e) => { if (!gone) setErr(errMsg(e)); });
    return () => { gone = true; };
  }, [defId]);
  return (
    <div className="dark h-full w-full flex flex-col text-page-text" style={{ background: "var(--page-bg)" }} data-tour="wf-editor-screen">
      {err ? (
        <div className="p-6 text-sm text-red-400">
          {err} — <button onClick={onBack} className="underline bg-transparent border-0 cursor-pointer text-page-text">back to processes</button>
        </div>
      ) : !def ? <div className="p-6 text-sm text-page-muted">Loading…</div>
        : <Editor key={def.id} def={def} onBack={onBack} onSaved={() => {}} />}
    </div>
  );
}

// ── processes tab (the list; opening one switches to the full-bleed editor) ──
function ProcessesTab({ onSelectId }) {
  const [defs, setDefs] = useState(null);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const load = useCallback(() => {
    wfEngine("list_definitions", {}).then((r) => { setDefs(r.definitions || []); setErr(""); }).catch((e) => { setErr(errMsg(e)); setDefs((p) => p ?? []); });
  }, []);
  useEffect(load, [load]);

  return (
    <div className="space-y-4 max-w-3xl" data-tour="wf-processes">
      <div className="flex items-center gap-2">
        <div className="text-sm text-page-muted">A process is a graph of actions assigned to roles, people or agents — approvals, handovers, parallel work — advancing as each step completes.</div>
        <div className="flex-1" />
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-page-muted hover:text-nav-active bg-transparent border-0 cursor-pointer"><RefreshCw size={13} />Refresh</button>
        {!isPortal() && <button className={btnPrimary} onClick={() => setCreating(true)} data-tour="wf-new"><Plus size={14} />New process</button>}
      </div>
      {err ? <div className="text-sm text-red-600">{err}</div> : null}
      {defs === null ? <div className="text-sm text-page-muted">Loading…</div> :
        !defs.length ? (
          <div className={`${card} p-10 text-center space-y-2`}>
            <Route size={32} className="mx-auto text-page-muted" />
            <div className="font-medium text-page-text">Design your first business process</div>
            <div className="text-sm text-page-muted">Chain actions across roles, people and agents; publish it and runs start from My Work or from an email trigger.</div>
            <button className={`${btnPrimary} mt-2`} onClick={() => setCreating(true)}><Plus size={14} />New process</button>
          </div>
        ) : (
          <div className="space-y-2">
            {defs.map((d) => {
              const g = jsonish(d.graph, {}) || {};
              const steps = (g.nodes || []).filter((n) => n.type === "task" || n.type === "decision").length;
              const agents = (g.nodes || []).filter((n) => n.agent).length;
              return (
                <div key={d.id} className={`${card} p-4 flex items-center gap-3 flex-wrap cursor-pointer hover:border-nav-active/60`} data-wf-def={d.id}
                  onClick={() => onSelectId(d.id)}>
                  <ListChecks size={16} className="text-nav-active" />
                  <div className="font-medium text-page-text">{d.name}</div>
                  <StatusBadge s={d.status} />
                  <span className="text-xs text-page-muted">{steps} step{steps === 1 ? "" : "s"}</span>
                  {agents ? <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: "#8b5cf6" }}><Bot size={12} />{agents}</span> : null}
                  {Number(d.running_count) > 0 ? <span className="text-xs text-nav-active font-medium">{d.running_count} running</span> : null}
                  <div className="flex-1" />
                  <span className="text-[11px] text-page-muted">updated {fmtTime(d.updated_at)}</span>
                  <button className="text-page-muted hover:text-red-600 bg-transparent border-0 cursor-pointer" title="Delete"
                    onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete process '${d.name}'?`)) wfEngine("delete_definition", { id: d.id }).then(load).catch((e2) => setErr(errMsg(e2))); }}>
                    <Trash2 size={14} /></button>
                </div>
              );
            })}
          </div>
        )}
      {creating ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCreating(false)} />
          <div className={`relative ${card} shadow-xl p-5 space-y-4`} style={{ width: 420, maxWidth: "calc(100vw - 32px)" }}>
            <div className="font-semibold text-page-text">New process</div>
            <label className="block text-sm"><FieldLabel>Name</FieldLabel>
              <input value={newName} autoFocus onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Invoice approval" className={inputCls}
                onKeyDown={async (e) => { if (e.key === "Enter" && newName.trim()) { const r = await wfEngine("save_definition", { name: newName.trim(), graph: NEW_GRAPH }); setCreating(false); setNewName(""); load(); onSelectId(r.definition.id); } }} data-tour="wf-new-name" /></label>
            <div className="flex justify-end gap-2">
              <button className={btnGhost} onClick={() => setCreating(false)}>Cancel</button>
              <button className={btnPrimary} disabled={!newName.trim()} onClick={async () => {
                try { const r = await wfEngine("save_definition", { name: newName.trim(), graph: NEW_GRAPH }); setCreating(false); setNewName(""); load(); onSelectId(r.definition.id); }
                catch (e) { setErr(errMsg(e)); setCreating(false); }
              }} data-tour="wf-create">Create</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── request kinds (the "Build with an agent" pattern, same as Apps/Agents) ───
const WORKFLOW_REQUEST_KINDS = [
  {
    kind: "new_workflow", label: "New workflow", icon: Sparkles, suggestFeatures: true,
    hint: "Describe the business process — an agent designs it on the canvas (steps, human approvals, AI agent actors, triggers) and publishes it. You'll find it here when it's done.",
    titlePlaceholder: "What should the process be called / do?",
    detailsPlaceholder: "Describe the process in plain language — the steps, who approves what, which steps AI agents should handle, what starts a run…",
  },
  {
    kind: "change_workflow", label: "Change a workflow", icon: Wrench,
    hint: "Describe the change to an existing process (name it, or use the ✨ action on a process row to prefill).",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Which process, and what should change — steps, assignments, outcomes, triggers…",
  },
];

// ── page shell — left rail + content, the Apps/Agents layout ────────────────
export default function Workflows() {
  const { user } = useAuth();
  // Simple view: the page is the My Work inbox — the process explorer, the
  // designer entry points and the build CTA are Advanced affordances. Deep
  // links (?tab=processes&def=…) still render; only the rail is trimmed.
  const { simple } = useUiView();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "mywork";
  const defId = searchParams.get("def") || null;
  const [myCount, setMyCount] = useState(0);
  const [unavailable, setUnavailable] = useState(null);
  const [railOpen, setRailOpen] = useState(() => localStorage.getItem("wf_rail_open") !== "0");
  const [defs, setDefs] = useState(null);
  const [buildDialog, setBuildDialog] = useState(null); // { kind, preset? }
  const [buildFiled, setBuildFiled] = useState(false);
  const setTab = (t) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); n.delete("def"); return n; }, { replace: true });
  const setDefId = (id) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", "processes"); if (id) n.set("def", id); else n.delete("def"); return n; }, { replace: true });
  const toggleRail = () => setRailOpen((v) => { localStorage.setItem("wf_rail_open", v ? "0" : "1"); return !v; });

  const loadDefs = useCallback(() => {
    wfEngine("list_definitions", {}).then((r) => setDefs(r.definitions || [])).catch(() => setDefs((p) => p ?? []));
  }, []);

  // Probe once: a project without a postgres target (or Flow trouble) gets a
  // clear provisioning message instead of a dozen failing sub-fetches.
  useEffect(() => {
    let gone = false;
    wfEngine("list_definitions", {}).then((r) => { if (!gone) { setUnavailable(false); setDefs(r.definitions || []); } })
      .catch((e) => { if (!gone) setUnavailable(errMsg(e)); });
    return () => { gone = true; };
  }, []);

  const navItem = (active) =>
    `w-full flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm border-0 cursor-pointer text-left ${active ? "bg-nav-active/10 text-nav-active font-medium" : "bg-transparent text-page-text hover:bg-card-border/30"}`;

  const content = unavailable === null ? <div className="p-6 text-sm text-page-muted">Loading…</div> : unavailable ? (
    <div className="p-6">
      <div className={`${card} p-8 max-w-2xl text-center space-y-2`}>
        <AlertTriangle size={28} className="mx-auto text-amber-500" />
        <div className="font-medium text-page-text">Workflows isn't provisioned for this project yet</div>
        <div className="text-sm text-page-muted">The process engine stores its data in the project's Postgres data target — add one (Pipeline → save node) and reload. Details: {String(unavailable).slice(0, 200)}</div>
      </div>
    </div>
  ) : tab === "processes" && defId ? (
    // The dark designer studio fills the content area (Pipeline treatment).
    <EditorScreen defId={defId} onBack={() => { setDefId(null); loadDefs(); }} />
  ) : (
    <div className="p-6 overflow-y-auto h-full min-h-0">
      {tab === "processes" ? <ProcessesTab onSelectId={setDefId} />
        : tab === "roles" ? <RolesTab />
        : <MyWorkTab me={user} onCounts={setMyCount} />}
    </div>
  );

  return (
    <div className="h-full min-h-0 flex" data-tour="wf-page">
      {!railOpen && (
        <button type="button" onClick={toggleRail} title="Show workflows list" data-testid="wf-rail-open"
          className="absolute z-10 mt-3 ml-2 text-page-muted hover:text-page-text bg-card-bg border border-card-border rounded-md cursor-pointer p-1.5">
          <PanelLeftOpen size={16} />
        </button>
      )}
      {/* Left rail: processes explorer — same shell as the Apps/Agents rails */}
      <aside data-tour="wf-rail" className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <Route size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Workflows</span>
          <button type="button" onClick={loadDefs} title="Refresh" className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <RefreshCw size={14} />
          </button>
          <button type="button" onClick={toggleRail} title="Collapse" data-testid="wf-rail-close" className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="space-y-0.5 mb-3">
            <button type="button" onClick={() => setTab("mywork")} data-tour="wf-tab-mywork" className={navItem(tab === "mywork")}>
              <Inbox size={14} />My Work{myCount ? <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-nav-active text-white text-[10px] leading-[18px] text-center">{myCount}</span> : null}
            </button>
            {!simple && (
              <button type="button" onClick={() => { setTab("processes"); }} data-tour="wf-tab-processes" className={navItem(tab === "processes" && !defId)}>
                <ListChecks size={14} />All processes
              </button>
            )}
            {!simple && (
              <button type="button" onClick={() => setTab("roles")} data-tour="wf-tab-roles" className={navItem(tab === "roles")}>
                <Users size={14} />Roles
              </button>
            )}
          </div>
          {simple ? null : <>
          <div className="px-2.5 pt-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-page-muted">My processes</div>
          {defs === null ? <div className="text-sm text-page-muted px-2.5 py-2">Loading…</div>
            : !defs.length ? <div className="text-xs text-page-muted px-2.5 py-2">No processes yet — build one below or design it on the canvas.</div>
            : defs.map((d) => {
              const g = jsonish(d.graph, {}) || {};
              const agents = (g.nodes || []).filter((n) => n.agent).length;
              return (
                <div key={d.id} data-wf-rail-def={d.id}
                  className={"group w-full flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm cursor-pointer " + (defId === d.id ? "bg-nav-active/10 text-nav-active" : "text-page-text hover:bg-card-border/30")}
                  onClick={() => setDefId(d.id)}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: STATUS_COLOR[d.status] || "#94a3b8" }} title={d.status} />
                  <span className="truncate flex-1">{d.name}</span>
                  {agents ? <span className="inline-flex items-center gap-0.5 text-[10px] font-medium shrink-0" style={{ color: "#8b5cf6" }}><Bot size={11} />{agents}</span> : null}
                  {Number(d.running_count) > 0 ? <span className="text-[10px] text-nav-active font-semibold shrink-0">{d.running_count}▶</span> : null}
                  <button type="button" title="Request a change to this workflow"
                    className="opacity-0 group-hover:opacity-100 text-page-muted hover:text-nav-active bg-transparent border-0 cursor-pointer p-0.5 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBuildDialog({ preset: { kind: "change_workflow", title: `Change "${d.name}"`, details: `TARGET WORKFLOW: ${d.id} — ${d.name}\n\n` } });
                    }}>
                    <Sparkles size={12} />
                  </button>
                </div>
              );
            })}
          </>}
        </div>
        {/* Rail footer: file a request for an agent-built workflow. */}
        {!simple && (
          <div className="p-2 border-t border-card-border">
            <button type="button" data-testid="wf-request-new" onClick={() => setBuildDialog({ preset: { kind: "new_workflow" } })}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer">
              <Sparkles size={14} /> Build a workflow
            </button>
          </div>
        )}
      </aside>

      <main className="flex-1 min-w-0 min-h-0 relative bg-page-bg">{content}</main>

      {buildDialog ? (
        <AppRequestDialog
          kinds={WORKFLOW_REQUEST_KINDS}
          preset={buildDialog.preset || {}}
          title="Build a workflow"
          onClose={() => setBuildDialog(null)}
          onCreated={() => { setBuildDialog(null); setBuildFiled(true); }}
        />
      ) : null}
      {buildFiled ? (
        <div className="fixed bottom-5 right-5 z-50 rounded-lg border border-card-border bg-card-bg shadow-lg px-4 py-3 text-sm text-page-text flex items-center gap-3">
          <CheckCircle2 size={16} className="text-green-600" />
          <span>Workflow request filed — an agent is on it. Track progress on the <b>Board</b>.</span>
          <button onClick={() => setBuildFiled(false)} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"><X size={14} /></button>
        </div>
      ) : null}
    </div>
  );
}
